import { AdminApiError, adminClient } from '@/api/clients/admin-client'
import type { AdminPageData } from '@/shared/types/admin'
import type { ApiResponse } from '@/shared/types/http'

export type TenantId = string | number

export interface TenantRecord {
  id?: TenantId
  datasourceId?: TenantId
  tenantDomain?: string
  tenantName: string
  username: string
  password?: string
  remark?: string
  userId?: TenantId
  tenantMode: 0 | 1
  status: 0 | 1
  roleIdList: TenantId[]
  createDate?: string
}

export interface TenantListItem {
  tenantCode: TenantId
  tenantName: string
  tenantDomain?: string
  tenantMode: 0 | 1
  datasourceId?: TenantId
  status: 0 | 1
}

export interface TenantRoleRecord {
  id?: TenantId
  name: string
  remark?: string
  menuIdList: TenantId[]
  createDate?: string
}

export interface TenantDataSourceRecord {
  id?: TenantId
  name: string
  driverClassName: string
  url: string
  username: string
  password?: string
  createDate?: string
}

const requirePage = <T>(response: { data: ApiResponse<AdminPageData<T>> }, label: string): AdminPageData<T> => {
  const data = response.data.data
  if (!data || !Array.isArray(data.list) || typeof data.total !== 'number') {
    throw new AdminApiError(`${label}分页响应格式错误`, response.data.code)
  }
  return data
}

const requireList = <T>(response: { data: ApiResponse<T[]> }, label: string): T[] => {
  if (!Array.isArray(response.data.data)) {
    throw new AdminApiError(`${label}列表响应格式错误`, response.data.code)
  }
  return response.data.data
}

const requireRecord = <T>(response: { data: ApiResponse<T> }, label: string): T => {
  if (!response.data.data || typeof response.data.data !== 'object') {
    throw new AdminApiError(`${label}详情响应格式错误`, response.data.code)
  }
  return response.data.data
}

const idPath = (id: TenantId): string => {
  const value = String(id).trim()
  if (!value) throw new Error('租户记录 ID 不合法')
  return encodeURIComponent(value)
}

export const tenantApi = {
  async page(params: { page: number; limit: number; tenantName?: string; status?: number | string }): Promise<AdminPageData<TenantRecord>> {
    return requirePage(await adminClient.get<ApiResponse<AdminPageData<TenantRecord>>>('/sys/tenant/page', { params }), '租户')
  },
  async list(): Promise<TenantListItem[]> {
    return requireList(await adminClient.get<ApiResponse<TenantListItem[]>>('/sys/tenant/list'), '租户')
  },
  async current(): Promise<TenantRecord> {
    return requireRecord(await adminClient.get<ApiResponse<TenantRecord>>('/sys/tenant/info'), '当前租户')
  },
  async detail(id: TenantId): Promise<TenantRecord> {
    return requireRecord(await adminClient.get<ApiResponse<TenantRecord>>(`/sys/tenant/${idPath(id)}`), '租户')
  },
  async create(payload: TenantRecord): Promise<void> { await adminClient.post('/sys/tenant', payload) },
  async update(payload: TenantRecord): Promise<void> { await adminClient.put('/sys/tenant', payload) },
  async remove(ids: TenantId[]): Promise<void> { await adminClient.delete('/sys/tenant', { data: ids }) }
}

export const tenantRoleApi = {
  async page(params: { page: number; limit: number; name?: string }): Promise<AdminPageData<TenantRoleRecord>> {
    return requirePage(await adminClient.get<ApiResponse<AdminPageData<TenantRoleRecord>>>('/sys/tenant/role/page', { params }), '租户套餐')
  },
  async list(): Promise<TenantRoleRecord[]> {
    return requireList(await adminClient.get<ApiResponse<TenantRoleRecord[]>>('/sys/tenant/role/list'), '租户套餐')
  },
  async detail(id: TenantId): Promise<TenantRoleRecord> {
    return requireRecord(await adminClient.get<ApiResponse<TenantRoleRecord>>(`/sys/tenant/role/${idPath(id)}`), '租户套餐')
  },
  async create(payload: TenantRoleRecord): Promise<void> { await adminClient.post('/sys/tenant/role', payload) },
  async update(payload: TenantRoleRecord): Promise<void> { await adminClient.put('/sys/tenant/role', payload) },
  async remove(ids: TenantId[]): Promise<void> { await adminClient.delete('/sys/tenant/role', { data: ids }) }
}

export const tenantDataSourceApi = {
  async page(params: { page: number; limit: number; name?: string }): Promise<AdminPageData<TenantDataSourceRecord>> {
    return requirePage(await adminClient.get<ApiResponse<AdminPageData<TenantDataSourceRecord>>>('/sys/tenant/datasource/page', { params }), '租户数据源')
  },
  async list(): Promise<TenantDataSourceRecord[]> {
    return requireList(await adminClient.get<ApiResponse<TenantDataSourceRecord[]>>('/sys/tenant/datasource/list'), '租户数据源')
  },
  async detail(id: TenantId): Promise<TenantDataSourceRecord> {
    return requireRecord(await adminClient.get<ApiResponse<TenantDataSourceRecord>>(`/sys/tenant/datasource/${idPath(id)}`), '租户数据源')
  },
  async create(payload: TenantDataSourceRecord): Promise<void> { await adminClient.post('/sys/tenant/datasource', payload) },
  async remove(ids: TenantId[]): Promise<void> { await adminClient.delete('/sys/tenant/datasource', { data: ids }) }
}
